import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, Observable } from 'rxjs';
import { ReleaseNotesApiClient } from 'app/core/api-clients';
import { ReleaseNoteModel } from 'app/core/models';

/**
 * Dialog visibility, the once-per-release announcement, and the `New` snapshot.
 *
 * The claim is issued once, on the help menu's first render. A `200` means this browser won the
 * right to announce the latest release and the dialog opens by itself; anything else stays silent.
 * The durable once-per-release guarantee is the server's conditional upsert, not anything here.
 *
 * The announcement needs no route rule of its own: everything is driven by the help menu, and the
 * help menu is absent from exactly the pages that must not be interrupted.
 */
@Injectable({ providedIn: 'root' })
export class ReleaseNotesStore {
  private readonly _client = inject(ReleaseNotesApiClient);

  private readonly _isDialogOpenSource$ = new BehaviorSubject<boolean>(false);

  private readonly _claimedVersionSource$ = new BehaviorSubject<string | null>(null);

  /**
   * How many times one session will try to claim before giving up for good.
   *
   * There is no backoff because there is no timer: a retry only happens when the user navigates to
   * another page carrying the help menu, so the interval is human-paced already. The cap exists for
   * the case where the failure is not transient — the release notes folder missing from the
   * deployment, say, which returns `503` to every request. Without it, every navigation for the rest
   * of the session fires another doomed claim.
   */
  private static readonly _maxClaimAttempts = 3;

  /**
   * Whether a claim is in flight. De-duplication only: correctness does not depend on it, since a
   * lost guard costs one extra claim that the database answers with "already claimed" and no write.
   */
  private _isClaimRequested = false;

  /** Attempts spent, counted against {@link _maxClaimAttempts}. */
  private _claimAttempts = 0;

  readonly isDialogOpen$: Observable<boolean> = this._isDialogOpenSource$.pipe(distinctUntilChanged());

  /**
   * The version this session claimed, or `null` when nothing was claimed. The dialog's rail tags it
   * `New`.
   *
   * It cannot be derived from the releases themselves: the claim advances the server-side marker
   * before the dialog loads its first page, so any read flag on the list would already be spent by
   * the time the rail rendered.
   */
  readonly claimedVersion$: Observable<string | null> = this._claimedVersionSource$.pipe(distinctUntilChanged());

  /** Called by the help menu on first render. Repeat calls are ignored, so navigation cannot refetch. */
  ensureClaimed(): void {
    if (this._isClaimRequested || this._claimAttempts >= ReleaseNotesStore._maxClaimAttempts) {
      return;
    }

    this._isClaimRequested = true;
    this._claimAttempts += 1;

    this._client.claimLatest().subscribe({
      next: (claimed: ReleaseNoteModel | null): void => {
        // 204 arrives as a null body: nothing published, or this release is already claimed.
        if (claimed === null) {
          return;
        }

        this._claimedVersionSource$.next(claimed.version);
        this.openDialog();
      },
      // A failed claim wrote nothing, so the opportunity is still there. Releasing the guard lets a
      // later navigation retry rather than losing the announcement to one bad request — up to
      // _maxClaimAttempts, after which the top of ensureClaimed refuses to start another.
      error: (): void => {
        this._isClaimRequested = false;
      },
    });
  }

  openDialog(): void {
    this._isDialogOpenSource$.next(true);
  }

  closeDialog(): void {
    this._isDialogOpenSource$.next(false);
  }
}
